import React, { useEffect, useState, useContext } from 'react';

import keys from 'lodash/keys';
import includes from 'lodash/includes';
import isNil from 'lodash/isNil';

import { Box, Grid, InputAdornment, Switch, TextField, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import LinkIcon from '@mui/icons-material/Link';
import WallpaperIcon from '@mui/icons-material/Wallpaper';
import { SelectChangeEvent } from '@mui/material/Select';
import { EventBanner, PlainObject } from '../data_types/types';
import { DataContext } from '../data/DataContext';
import { DataContextType } from '../data_types/interfaces';
import { VALID_FIELD } from './AddEventForm';
import { DS } from '../shared/Dashboard';


interface EventGeneralDetailsFormProps {
    isEditMode: boolean;
    formData: any; handleInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent) => void;
    handleCheckboxChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleCustomBackgroundChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onValidationChange: (isValid: PlainObject) => void;
}

interface GeneralErrors {
    eventId: boolean | string;
    eventName: boolean | string;
    eventInfoURL: boolean | string;
}

// Optional link to an external page with more info about the event
const isValidInfoURL = (value: string) => {
    try {
        const url = new URL(value);
        return url.protocol === 'https:' || url.protocol === 'http:';
    } catch {
        return false;
    }
};

const EventGeneralDetailsForm: React.FC<EventGeneralDetailsFormProps> = ({ isEditMode, formData, handleInputChange, handleCheckboxChange, handleCustomBackgroundChange, onValidationChange }) => {
    const context = useContext<DataContextType | undefined>(DataContext);
    const { pendingEvents, privateEvents, publicEvents, archivedEvents, inviteEvents } = context || {};

    const [errors, setErrors] = useState<GeneralErrors>({
        eventId: VALID_FIELD,
        eventName: VALID_FIELD,
        eventInfoURL: VALID_FIELD,
    });

    // Validate form
    useEffect(() => {
        const knownEventIds = keys({
            ...pendingEvents,
            ...privateEvents,
            ...publicEvents,
            ...archivedEvents,
            ...inviteEvents
        });
        const normalizedKnownEventIds = knownEventIds.map((id) => id.trim().toLowerCase());
        const normalizedEventId = isNil(formData.eventID) ? '' : String(formData.eventID).trim().toLowerCase();

        const newErrors: GeneralErrors = {
            eventId: (
                isEditMode ?
                  VALID_FIELD
                : isNil(formData.eventID) || formData.eventID === '' ?
                    'Event ID is required'
                : includes(normalizedKnownEventIds, normalizedEventId) ?
                    'This event ID is already in use'
                : /^[a-z0-9_]*$/.test(String(formData.eventID)) ?
                    VALID_FIELD
                :
                    'Event IDs can only contain lowercase letters, numbers, and underscores'
            ),
            eventName: (
                isNil(formData.eventName) || formData.eventName === '' ?
                    'Event name is required'
                : /^[\w_@./#&+%\-\s]+$/gi.test(formData.eventName) ?
                    VALID_FIELD
                :
                    'Event name is not valid'
            ),
            eventInfoURL: (
                isNil(formData.eventInfoURL) || String(formData.eventInfoURL).trim() === '' ?
                    VALID_FIELD
                : isValidInfoURL(String(formData.eventInfoURL).trim()) ?
                    VALID_FIELD
                :
                    'Enter a full URL, e.g. https://globaldisc.golf'
            ),
        };

        setErrors(newErrors);
        onValidationChange(newErrors);

    }, [isEditMode, formData, pendingEvents, privateEvents, publicEvents, archivedEvents, inviteEvents, onValidationChange]);

    const hasCustomBackground = formData.banner === EventBanner.CustomBackground;

    return (
        <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
                <TextField
                    fullWidth
                    label="Event ID"
                    name="eventID"
                    value={formData.eventID}
                    onChange={handleInputChange}
                    required
                    disabled={isEditMode}
                    error={!!errors.eventId}
                    helperText={errors.eventId ? errors.eventId : ''}
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <TextField
                    fullWidth
                    label="Event Name"
                    name="eventName"
                    value={formData.eventName}
                    onChange={handleInputChange}
                    required
                    error={!!errors.eventName}
                    helperText={errors.eventName ? errors.eventName : ''}
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <TextField
                    fullWidth
                    type="datetime-local"
                    label="Start Time (UTC)"
                    name="eventStartTime"
                    value={formData.eventStartTime}
                    onChange={handleInputChange}
                    InputLabelProps={{ shrink: true }}
                    required
                    inputProps={{ min: new Date().toISOString().slice(0, 16) }}
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <TextField
                    fullWidth
                    type="datetime-local"
                    label="End Time (UTC)"
                    name="eventEndTime"
                    value={formData.eventEndTime}
                    onChange={handleInputChange}
                    InputLabelProps={{ shrink: true }}
                    required
                    inputProps={{ min: formData.eventStartTime }}
                />
            </Grid>
            <Grid item xs={12}>
                <TextField
                    fullWidth
                    type="url"
                    label="Event info (optional)"
                    name="eventInfoURL"
                    placeholder="https://globaldisc.golf"
                    value={formData.eventInfoURL || ''}
                    onChange={handleInputChange}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <LinkIcon sx={{ fontSize: 20, color: DS.textFaint }} />
                            </InputAdornment>
                        ),
                    }}
                    error={!!errors.eventInfoURL}
                    helperText={errors.eventInfoURL ? errors.eventInfoURL : 'Opens from the "Additional information" button in the game'}
                />
            </Grid>
            <Grid item xs={12}>
                <Box
                    component="label"
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        px: 2,
                        py: 1.25,
                        borderRadius: '8px',
                        border: `1px solid ${hasCustomBackground ? alpha(DS.brand, 0.5) : DS.borderStrong}`,
                        background: hasCustomBackground ? alpha(DS.brand, 0.08) : 'transparent',
                        cursor: 'pointer',
                        transition: 'border-color 0.2s ease, background 0.2s ease',
                        '&:hover': { borderColor: hasCustomBackground ? DS.brand : DS.textFaint },
                    }}
                >
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        width: 36,
                        height: 36,
                        borderRadius: '8px',
                        background: hasCustomBackground ? alpha(DS.brand, 0.16) : DS.inset,
                        color: hasCustomBackground ? DS.brand : DS.textMuted,
                    }}>
                        <WallpaperIcon sx={{ fontSize: 20 }} />
                    </Box>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: '14px', fontWeight: 600, color: DS.text }}>
                            Custom background
                        </Typography>
                        <Typography sx={{ fontSize: '12px', color: DS.textMuted }}>
                            Only if this event has its own panel image. The game loads{' '}
                            <Box component="span" sx={{ fontFamily: 'monospace', color: DS.text }}>
                                {formData.eventID ? `${formData.eventID}.png` : '<event id>.png'}
                            </Box>
                        </Typography>
                    </Box>
                    <Switch
                        edge="end"
                        checked={hasCustomBackground}
                        onChange={handleCustomBackgroundChange}
                        inputProps={{ 'aria-label': 'Event uses a custom background for the event panel' }}
                    />
                </Box>
            </Grid>

            {/* Additional fields... 
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  name="eventFixedSchedule"
                  checked={formData.eventFixedSchedule}
                  onChange={handleCheckboxChange}
                />
              }
              label="Fixed Schedule"
            />
            <FormControlLabel
              control={
                <Checkbox
                  name="eventVisibility"
                  checked={formData.eventVisibility}
                  onChange={handleCheckboxChange}
                />
              }
              label="Visible to Users"
            />
          </Grid>
          */}
        </Grid>
    );
};

export default EventGeneralDetailsForm;
